import type { ColumnDef } from "@tanstack/react-table";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { format } from "date-fns";
import type { Recipe } from "@/types/recipe";

/**
 * Column definitions used exclusively for Excel/CSV export of prep recipes.
 * Work directly on the raw Recipe API type for round-trip import fidelity.
 */
export function getPrepRecipeExportColumns(i18n: I18n): ColumnDef<Recipe, unknown>[] {
  return [
    {
      id: "id",
      accessorKey: "id",
      meta: { label: i18n._(msg`ID`) },
    },
    {
      id: "name",
      accessorKey: "name",
      meta: { label: i18n._(msg`Name`) },
    },
    {
      id: "slug",
      accessorKey: "slug",
      meta: { label: i18n._(msg`Prep Recipe ID`) },
    },
    {
      id: "category",
      accessorKey: "category",
      meta: { label: i18n._(msg`Category`) },
    },
    {
      id: "subcategory",
      accessorKey: "subcategory",
      meta: { label: i18n._(msg`Subcategory`) },
    },
    {
      id: "description",
      accessorKey: "description",
      meta: { label: i18n._(msg`Description`) },
    },
    {
      id: "difficulty",
      accessorKey: "difficulty",
      meta: { label: i18n._(msg`Difficulty`) },
    },
    {
      id: "prepTimeMinutes",
      accessorKey: "prepTimeMinutes",
      meta: { label: i18n._(msg`Prep Time (min)`) },
    },
    {
      id: "cookTimeMinutes",
      accessorKey: "cookTimeMinutes",
      meta: { label: i18n._(msg`Cook Time (min)`) },
    },
    {
      id: "restTimeMinutes",
      accessorKey: "restTimeMinutes",
      meta: { label: i18n._(msg`Rest Time (min)`) },
    },
    {
      id: "servings",
      accessorKey: "servings",
      meta: { label: i18n._(msg`Yield (Servings)`) },
    },
    {
      id: "servingSize",
      accessorKey: "servingSize",
      meta: { label: i18n._(msg`Serving Size`) },
    },
    {
      id: "ingredientCount",
      accessorFn: (row) =>
        row._count?.ingredients ?? row.ingredients?.length ?? 0,
      meta: { label: i18n._(msg`# Ingredients`) },
    },
    {
      id: "tips",
      accessorKey: "tips",
      meta: { label: i18n._(msg`Prep Notes / Tips`) },
    },
    {
      id: "labelingRequirements",
      accessorKey: "labelingRequirements",
      meta: { label: i18n._(msg`Labeling Requirements`) },
    },
    {
      id: "status",
      accessorFn: (row) =>
        !row.isActive
          ? i18n._(msg`Archived`)
          : row.isPublished
            ? i18n._(msg`Published`)
            : i18n._(msg`Draft`),
      meta: { label: i18n._(msg`Status`) },
    },
    {
      id: "allergenCount",
      accessorFn: (row) => row.allergens?.length ?? 0,
      meta: { label: i18n._(msg`Allergen Count`) },
    },
    {
      id: "allergens",
      accessorFn: (row) =>
        (row.allergens ?? []).map((a) => a.code).join(", "),
      meta: { label: i18n._(msg`Allergens`) },
    },
    {
      id: "version",
      accessorKey: "version",
      meta: { label: i18n._(msg`Version`) },
    },
    {
      id: "createdAt",
      accessorFn: (row) => format(new Date(row.createdAt), "yyyy-MM-dd HH:mm"),
      meta: { label: i18n._(msg`Created At`) },
    },
    {
      id: "updatedAt",
      accessorFn: (row) => format(new Date(row.updatedAt), "yyyy-MM-dd HH:mm"),
      meta: { label: i18n._(msg`Updated At`) },
    },
  ];
}
